import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";
import { PageShell, PageHeader } from "../components/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
    const { lang } = useLanguage();
    const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle | sending | success | error

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) return;
        setStatus("sending");
        try {
            await axios.post(`${API}/contact`, { ...form, locale: lang });
            setStatus("success");
            setForm({ name: "", email: "", organization: "", message: "" });
        } catch (err) {
            setStatus("error");
        }
    };

    const inputClass = "w-full bg-transparent border-0 border-b border-[color:var(--line-strong)] py-4 text-[17px] md:text-[19px] text-[color:var(--navy)] placeholder:text-[color:var(--warm-gray-2)] focus:outline-none focus:border-[color:var(--navy)] transition-colors duration-500";

    return (
        <PageShell testId="page-contact">
            <PageHeader
                overline={pick3(t3.contact.overline, lang)}
                title={pick3(t3.contact.title, lang)}
                kicker={pick3(t3.contact.lede, lang)}
            />

            <form onSubmit={onSubmit} className="mt-6 grid grid-cols-12 gap-6 max-w-[820px]">
                <div className="col-span-12 md:col-span-6">
                    <label htmlFor="contact-name-input" className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] block mb-3">{pick3(t3.contact.name, lang)}</label>
                    <input
                        id="contact-name-input"
                        data-testid="contact-name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className={inputClass}
                        placeholder="—"
                    />
                </div>
                <div className="col-span-12 md:col-span-6">
                    <label htmlFor="contact-email-input" className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] block mb-3">{pick3(t3.contact.email, lang)}</label>
                    <input
                        id="contact-email-input"
                        data-testid="contact-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className={inputClass}
                        placeholder="—"
                    />
                </div>
                <div className="col-span-12">
                    <label htmlFor="contact-org-input" className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] block mb-3">{pick3(t3.contact.org, lang)}</label>
                    <input
                        id="contact-org-input"
                        data-testid="contact-org"
                        value={form.organization}
                        onChange={(e) => setForm({ ...form, organization: e.target.value })}
                        className={inputClass}
                        placeholder="—"
                    />
                </div>
                <div className="col-span-12">
                    <label htmlFor="contact-message-input" className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] block mb-3">{pick3(t3.contact.message, lang)}</label>
                    <textarea
                        id="contact-message-input"
                        data-testid="contact-message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className={inputClass + " resize-none"}
                        placeholder="—"
                    />
                </div>
                <div className="col-span-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mt-4">
                    <div className="text-[12px] font-mono tracking-[0.22em] uppercase text-[color:var(--warm-gray-2)] max-w-md">
                        {status === "success" && <span className="text-[color:var(--navy)]">{pick3(t3.contact.success, lang)}</span>}
                        {status === "error"   && <span className="text-[color:var(--destructive)]">{pick3(t3.contact.error, lang)}</span>}
                        {status !== "success" && status !== "error" && <span>{pick3(t3.contact.note, lang)}</span>}
                    </div>
                    <motion.button
                        data-testid="contact-submit"
                        type="submit"
                        disabled={status === "sending"}
                        className="btn-editorial disabled:opacity-60"
                        whileHover={{ letterSpacing: "0.30em" }}
                        transition={{ duration: 0.4 }}
                    >
                        {status === "sending" ? pick3(t3.contact.sending, lang) : pick3(t3.contact.submit, lang)}
                        <span aria-hidden className="w-6 h-px bg-[color:var(--ivory)]" />
                    </motion.button>
                </div>
            </form>
        </PageShell>
    );
}
